import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Grid,
  Paper,
  Divider,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  useTheme,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "./store";
import {
  People as PeopleIcon,
  Payment as PaymentIcon,
  CardMembership as CardIcon,
  Schedule as ScheduleIcon,
  FitnessCenter as FitnessIcon,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
  AttachMoney as PriceIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  MoneyOff as MoneyOffIcon,
  Queue as QueueIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  Menu as MenuIcon,
  EventNote as EventNoteIcon,
  AccountBalanceWallet as WalletIcon,
  ConfirmationNumber as TicketIcon,
  MailOutline as MailOutlineIcon,
  AdminPanelSettings as AdminIcon,
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
} from "@mui/icons-material";
import { DeleteUsers as LessonManagement } from "./LessonAttendies";
import Payments from "./components/Admin/Payments";
import UserPassesComponent from "./components/Admin/UserPassesComponent";
import ScheduledLessonManagement from "./components/Admin/ScheduledLessonManagement";
import ConstLessonManagement from "./components/Admin/ConstLessonManagement";
import LessonTypeManagement from "./components/Admin/LessonTypeManagement";
import LessonCategoryManagement from "./components/Admin/LessonCategoryManagement";
import PriceListCategoryManagement from "./components/Admin/PriceListCategoryManagement";
import PassTypeManagement from "./components/Admin/PassTypeManagement";
import DebtManagement from "./components/Admin/DebtManagement";
import WaitlistManagement from "./components/Admin/WaitlistManagement";
import EmailManagement from "./components/Admin/EmailManagement";
import EmailHistory from "./components/Admin/EmailHistory";
import RoleManagement from "./components/Admin/RoleManagement";
import TrainerPermissionManagement from "./components/Admin/TrainerPermissionManagement";
import EventManagement from "./components/Admin/EventManagement";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Admin = () => {
  const navigate = useNavigate();
  const [categoryValue, setCategoryValue] = useState(0);
  const [subTabValue, setSubTabValue] = useState(0);
  const [isUserListOpen, setUserListOpen] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [isPassManagementOpen, setPassManagementOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery("(max-width:600px)");
  
  // Get user data from store
  const name = useAuthStore((state) => state.name);
  const lastName = useAuthStore((state) => state.lastName);
  const email = useAuthStore((state) => state.email);
  const fullName = name && lastName ? `${name} ${lastName}` : name || "";
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const shouldSeeAdminPanel = useAuthStore((state) => state.shouldSeeAdminPanel);
  const isTrainer = useAuthStore((state) => state.isTrainer);
  const isGongInstructor = useAuthStore((state) => state.isGongInstructor);
  const roles = useAuthStore((state) => state.roles);
  const trainerLessonTypes = useAuthStore((state) => state.trainerLessonTypes);
  const fetchUserRoles = useAuthStore((state) => state.fetchUserRoles);
  const fetchTrainerLessonTypes = useAuthStore((state) => state.fetchTrainerLessonTypes);

  // Get user role display text
  const getUserRoleText = () => {
    if (isAdmin) return "Administrator";
    if (isGongInstructor) return "Instruktor Gongów";
    if (isTrainer) return "Trener";
    return "Użytkownik";
  };

  // Fetch user roles on component mount
  React.useEffect(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  // Fetch trainer lesson types if user is a trainer or gong instructor
  React.useEffect(() => {
    if (isTrainer || isGongInstructor) {
      fetchTrainerLessonTypes();
    }
  }, [isTrainer, isGongInstructor, fetchTrainerLessonTypes]);

  const handleCategoryChange = (event, newValue) => {
    setCategoryValue(newValue);
    setSubTabValue(0);
  };

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCategoryClick = (categoryIndex) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  const handleNavigationClick = (categoryIndex, subIndex) => {
    setCategoryValue(categoryIndex);
    setSubTabValue(subIndex);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleOpenUserList = (lessonId) => {
    setSelectedLessonId(lessonId);
    setUserListOpen(true);
  };

  const handleOpenPassManagement = () => {
    setPassManagementOpen(true);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // All categories definition
  const allCategories = [
    {
      label: "Zajęcia",
      icon: <EventNoteIcon />,
      color: "#667eea",
      sections: [
        {
          label: "Zarządzanie zajęciami",
          icon: <ScheduleIcon />,
          component: <ScheduledLessonManagement />
        },
        {
          label: "Stałe zajęcia",
          icon: <CalendarIcon />,
          component: <ConstLessonManagement />
        },
        {
          label: isXs ? "Uczestnicy" : "Uczestnictwo w zajęciach",
          icon: <PeopleIcon />,
          component: <LessonManagement onAddUser={handleOpenUserList} />
        },
        {
          label: "Listy oczekujących",
          icon: <QueueIcon />,
          component: <WaitlistManagement />
        },
        {
          label: "Typy zajęć",
          icon: <FitnessIcon />,
          component: <LessonTypeManagement />
        },
        {
          label: "Kategorie zajęć",
          icon: <CategoryIcon />,
          component: <LessonCategoryManagement />
        },
      ]
    },
    {
      label: "Finanse",
      icon: <WalletIcon />,
      color: "#f093fb",
      sections: [
        {
          label: "Płatności",
          icon: <PaymentIcon />,
          component: <Payments />
        },
        {
          label: "Zarządzanie długami",
          icon: <MoneyOffIcon />,
          component: <DebtManagement />
        },
        {
          label: "Kategorie cennika",
          icon: <PriceIcon />,
          component: <PriceListCategoryManagement />
        },
      ]
    },
    {
      label: "Karnety",
      icon: <TicketIcon />,
      color: "#4facfe",
      sections: [
        {
          label: "Karnety użytkowników",
          icon: <CardIcon />,
          component: <UserPassesComponent />
        },
        {
          label: "Zarządzanie typami karnetów",
          icon: <SettingsIcon />,
          component: <PassTypeManagement />
        },
      ]
    },
    {
      label: "Eventy",
      icon: <EventNoteIcon />,
      color: "#ff9800",
      sections: [
        {
          label: "Zarządzanie Eventami",
          icon: <EventNoteIcon />,
          component: <EventManagement />
        },
      ]
    },
    {
      label: "Komunikacja",
      icon: <MailOutlineIcon />,
      color: "#fa709a",
      sections: [
        {
          label: "Wysyłka E-mail",
          icon: <EmailIcon />,
          component: <EmailManagement />
        },
        {
          label: "Historia E-maili",
          icon: <HistoryIcon />,
          component: <EmailHistory />
        },
      ]
    },
    {
      label: "Administracja",
      icon: <AdminIcon />,
      color: "#30cfd0",
      sections: [
        {
          label: "Zarządzanie Rolami",
          icon: <SecurityIcon />,
          component: <RoleManagement />
        },
        {
          label: "Uprawnienia Trenerów",
          icon: <FitnessIcon />,
          component: <TrainerPermissionManagement />
        },
      ]
    },
  ];

  // Filter categories based on user role
  const categories = React.useMemo(() => {
    // If user shouldn't see admin panel at all, show nothing
    if (!shouldSeeAdminPanel) {
      return [];
    }
    
    // Admins see everything
    if (isAdmin) {
      return allCategories;
    }
    
    // Trainers (non-admin) only see "Finanse" category with only "Płatności" section
    if (isTrainer && !isAdmin && !isGongInstructor) {
      const financeCategory = allCategories.find(cat => cat.label === "Finanse");
      if (financeCategory) {
        return [{
          ...financeCategory,
          sections: financeCategory.sections.filter(section => section.label === "Płatności")
        }];
      }
      return [];
    }
    
    // Gong instructors (non-admin) only see "Zajęcia" category with limited sections
    if (isGongInstructor && !isAdmin) {
      const lessonsCategory = allCategories.find(cat => cat.label === "Zajęcia");
      if (lessonsCategory) {
        return [{
          ...lessonsCategory,
          sections: lessonsCategory.sections.filter(section => 
            section.label === "Zarządzanie zajęciami" || 
            section.label === "Stałe zajęcia"
          )
        }];
      }
      return [];
    }
    
    // Default: show nothing for other roles
    return [];
  }, [isAdmin, isTrainer, isGongInstructor, shouldSeeAdminPanel]);

  const currentCategory = categories[categoryValue];
  const currentSection = currentCategory?.sections[subTabValue];

  // Mobile Bottom Navigation Component
  const mobileBottomNavigation = (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        display: { xs: 'block', md: 'none' },
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
      }}
      elevation={3}
    >
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          alignItems: 'center',
          py: 1,
          px: 0.5,
          gap: 0.5,
          '&::-webkit-scrollbar': {
            height: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 2,
          },
        }}
      >
        {/* Home button */}
        <Box
          onClick={handleBackToHome}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 60,
            flexShrink: 0,
            cursor: 'pointer',
            py: 0.5,
            px: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(76, 175, 80, 0.08)',
            },
          }}
        >
          <HomeIcon sx={{ fontSize: 24, color: '#4caf50' }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 500,
              color: '#4caf50',
              mt: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            Strona
          </Typography>
        </Box>

        {/* Category buttons */}
        {categories.map((category, categoryIndex) => (
          <Box
            key={categoryIndex}
            onClick={() => {
              setCategoryValue(categoryIndex);
              setSubTabValue(0);
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 60,
              flexShrink: 0,
              cursor: 'pointer',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              backgroundColor:
                categoryIndex === categoryValue
                  ? `${category.color}15`
                  : 'transparent',
              '&:hover': {
                backgroundColor:
                  categoryIndex === categoryValue
                    ? `${category.color}20`
                    : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Box sx={{ color: category.color }}>
              {React.cloneElement(category.icon, { sx: { fontSize: 24 } })}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                fontWeight: categoryIndex === categoryValue ? 600 : 500,
                color: categoryIndex === categoryValue ? category.color : 'text.secondary',
                mt: 0.25,
                textAlign: 'center',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
              }}
            >
              {category.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );

  // Mobile sections selector (shown below header when a category is selected)
  const mobileSectionSelector = (
    <Paper
      sx={{
        display: { xs: 'block', md: 'none' },
        mb: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          py: 1,
          px: 1,
          gap: 1,
          '&::-webkit-scrollbar': {
            height: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 2,
          },
        }}
      >
        {currentCategory?.sections.map((section, sectionIndex) => (
          <Box
            key={sectionIndex}
            onClick={() => setSubTabValue(sectionIndex)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              backgroundColor:
                sectionIndex === subTabValue
                  ? `${currentCategory.color}20`
                  : 'rgba(0, 0, 0, 0.02)',
              border: `2px solid ${
                sectionIndex === subTabValue
                  ? currentCategory.color
                  : 'transparent'
              }`,
              '&:hover': {
                backgroundColor:
                  sectionIndex === subTabValue
                    ? `${currentCategory.color}25`
                    : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Box
              sx={{
                color:
                  sectionIndex === subTabValue
                    ? currentCategory.color
                    : 'text.secondary',
              }}
            >
              {React.cloneElement(section.icon, { sx: { fontSize: 20 } })}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: sectionIndex === subTabValue ? 600 : 500,
                color:
                  sectionIndex === subTabValue
                    ? currentCategory.color
                    : 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              {section.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#2c3e50', mb: 3 }}>
        <Toolbar>
          <DashboardIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Panel Administracyjny - Natchnienie Studio
          </Typography>
          
          {/* User Info Display */}
          {fullName && (
            <Box 
              sx={{ 
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                mr: 2,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <Box sx={{ textAlign: 'right' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {fullName}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.7rem',
                  }}
                >
                  {getUserRoleText()}
                </Typography>
              </Box>
            </Box>
          )}
          
          <Button
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={handleBackToHome}
            sx={{
              minWidth: { xs: 'auto', sm: '140px' },
              px: { xs: 1, sm: 2 },
              '& .MuiButton-startIcon': {
                marginRight: { xs: 0, sm: 1 },
              },
              '& .MuiButton-startIcon > *': {
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              },
            }}
          >
            <Box 
              component="span" 
              sx={{ 
                display: { xs: 'none', sm: 'inline' }
              }}
            >
              Strona Główna
            </Box>
          </Button>
        </Toolbar>
      </AppBar>


      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Welcome Card */}
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: 'white', 
                fontWeight: 'bold', 
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' }
              }}
            >
              Panel Administracyjny
            </Typography>
            {fullName && (
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(255,255,255,0.95)',
                  fontSize: { xs: '1.1rem', sm: '1.35rem' },
                  fontWeight: 500,
                  mb: 1
                }}
              >
                Witaj, {fullName}
              </Typography>
            )}
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Zarządzanie studiem fitness - użytkownicy, zajęcia, płatności i karnety
            </Typography>
          </CardContent>
        </Card>

        {/* Desktop Navigation - Category Tabs */}
        {!isMobile && (
          <Paper sx={{ mb: 2, overflow: 'hidden' }}>
            <Tabs
              value={categoryValue}
              onChange={handleCategoryChange}
              variant="fullWidth"
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                },
                '& .MuiTab-root': {
                  minHeight: 64,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  py: 2,
                },
              }}
            >
              {categories.map((category, index) => (
                <Tab
                  key={index}
                  icon={category.icon}
                  label={category.label}
                  iconPosition="start"
                  sx={{
                    '& .MuiTab-iconWrapper': {
                      mr: 1,
                    },
                    '&.Mui-selected': {
                      color: category.color,
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: category.color,
                    },
                  }}
                />
              ))}
            </Tabs>
          </Paper>
        )}

        {/* Desktop Navigation - Sub Tabs */}
        {!isMobile && currentCategory && (
          <Paper sx={{ mb: 3, overflow: 'hidden' }}>
            <Tabs
              value={subTabValue}
              onChange={handleSubTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: currentCategory.color,
                },
                '& .MuiTab-root': {
                  minHeight: 56,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  minWidth: 140,
                  px: 2,
                },
                '& .Mui-selected': {
                  color: `${currentCategory.color} !important`,
                },
              }}
            >
              {currentCategory.sections.map((section, index) => (
                <Tab
                  key={index}
                  icon={section.icon}
                  label={section.label}
                  iconPosition="start"
                  sx={{
                    '& .MuiTab-iconWrapper': {
                      mr: 1,
                    },
                  }}
                />
              ))}
            </Tabs>
          </Paper>
        )}

        {/* Mobile Section Selector */}
        {isMobile && currentCategory && mobileSectionSelector}

        {/* Content */}
        {currentSection && (
          <Card sx={{ minHeight: '60vh', mb: { xs: 10, md: 0 } }}>
            <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
              {currentSection.component}
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Mobile Bottom Navigation */}
      {isMobile && mobileBottomNavigation}
    </Box>
  );
};

export default Admin;
